/**
 * Mahjong — deterministic seeding.
 *
 * Mahjong Solitaire lives or dies on REPRODUCIBLE, SOLVABLE deals, so we never
 * touch `Math.random` in the deal path. A 32-bit `mulberry32` PRNG seeded from
 * either a date stamp (the daily deal) or a fresh random number (practice)
 * drives the whole reverse-construction.
 */

/** A small, fast, well-distributed 32-bit PRNG. Returns floats in [0, 1). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function next(): number {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** FNV-1a hash of a string → a 32-bit unsigned seed. */
export function seedFrom(key: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < key.length; i += 1) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0 || 1;
}

/** Today's local-date stamp, `YYYYMMDD`. Refreshes at local midnight, matching
 *  how residents experience "today". */
export function todayStamp(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${y}${m}${d}`;
}

/** The daily deal seed for a layout — same for everyone in a community today. */
export function dailySeed(layoutId: string, date: Date = new Date()): number {
  return seedFrom(`${todayStamp(date)}-${layoutId}`);
}

/** A fresh, never-repeating practice seed. */
export function freshSeed(): number {
  return (Math.floor(Math.random() * 0xffffffff) >>> 0) || 1;
}
